import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getMe, login as apiLogin, logout as apiLogout, signup as apiSignup, addRole as apiAddRole, switchRole as apiSwitchRole } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await getMe();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await apiLogin({ email, password });
    setUser(response.data);
    return response.data;
  };

  const signup = async (email, password, profileType) => {
    // Ensure any prior session is gone before creating a new account so the
    // new cookie is never confused with the old identity.
    if (user) {
      try { await apiLogout(); } catch (_) {}
      setUser(null);
    }
    const response = await apiSignup({ email, password, profileType });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
  };

  const addRole = async (profileType) => {
    const response = await apiAddRole(profileType);
    setUser(response.data);
    return response.data;
  };

  const switchRole = async (profileType) => {
    const response = await apiSwitchRole(profileType);
    setUser(response.data);
    return response.data;
  };

  // Helper: check if user has a specific role
  const hasRole = (roleType) => {
    if (!user) return false;
    const types = user.profileTypes || [user.profileType];
    return types.includes(roleType);
  };

  // Helper: check if user has dual roles
  const isDualRole = () => {
    if (!user) return false;
    const types = user.profileTypes || [user.profileType];
    return types.length > 1;
  };

  const contextValue = useMemo(() => ({
    user, loading, login, signup, logout, checkAuth, addRole, switchRole, hasRole, isDualRole
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, loading]); // login/signup/logout/etc are stable function references

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
